// Based on https://redux-toolkit.js.org/tutorials/rtk-query/

// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Auth } from "aws-amplify";
import ariesConfig from "../configs/aries-configs";
import {
  FlightCrossSection,
  FlightInfo,
  FlightWind,
  Post,
  Route,
} from "./types";

// Define a service using a base URL and expected endpoints
export const dashboardApi = createApi({
  reducerPath: "dashboardApi",
  baseQuery: fetchBaseQuery({
    baseUrl: ariesConfig.baseUrl,
    prepareHeaders: async (headers) => {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();

      if (token) {
        headers.set("Authorization", `${token}`);
      }

      return headers;
    },
  }),
  tagTypes: ["Posts"],
  endpoints: (builder) => ({
    flightList: builder.query<FlightInfo[], string[]>({
      query: ([account, departureDateUtc]) => ({
        url: "/v1/dashboard",
        method: "POST",
        body: {
          method: "flight-list",
          account: account,
          departure_date_utc: departureDateUtc,
        },
      }),
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.map(
                ({ PK, SK }) => ({ type: "Posts", PK, SK } as const)
              ),
              { type: "Posts", PK: "PK_LIST", SK: "SK_LIST" },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Posts', PK: 'PK_LIST', SK: 'SK_LIST' }` is invalidated
            [{ type: "Posts", PK: "PK_LIST", SK: "SK_LIST" }],
    }),
    flightInfo: builder.query<FlightInfo, string[]>({
      query: ([callSign, departureDateUtc]) => ({
        url: "/v1/dashboard",
        method: "POST",
        body: {
          method: "flight-info",
          call_sign: callSign,
          departure_date_utc: departureDateUtc,
        },
      }),
      providesTags: (result, error, callSignAndDepartureDateUtc) => [
        {
          type: "Posts",
          PK: `flight#${callSignAndDepartureDateUtc[0]}`,
          SK: `account_date#${callSignAndDepartureDateUtc[0].slice(0, 3)}#${
            callSignAndDepartureDateUtc[1]
          }`,
        },
      ],
    }),
    flightDetail: builder.query<Route, string[]>({
      query: ([callSign, departureDateUtc]) => ({
        url: "/v1/dashboard",
        method: "POST",
        body: {
          method: "flight-detail",
          call_sign: callSign,
          departure_date_utc: departureDateUtc,
        },
      }),
      providesTags: (result, error, callSignAndDepartureDateUtc) => [
        {
          type: "Posts",
          PK: `flight#${callSignAndDepartureDateUtc[0]}`,
          SK: `account_date#${callSignAndDepartureDateUtc[0].slice(0, 3)}#${
            callSignAndDepartureDateUtc[1]
          }`,
        },
      ],
    }),
    flightWind: builder.query<FlightWind, string[]>({
      query: ([callSign, departureDateUtc]) => ({
        url: "/v1/dashboard",
        method: "POST",
        body: {
          method: "get-wind-data",
          call_sign: callSign,
          departure_date_utc: departureDateUtc,
        },
      }),
      providesTags: (result, error, callSignAndDepartureDateUtc) => [
        {
          type: "Posts",
          PK: `flight#${callSignAndDepartureDateUtc[0]}`,
          SK: `account_date#${callSignAndDepartureDateUtc[0].slice(0, 3)}#${
            callSignAndDepartureDateUtc[1]
          }#WIND`,
        },
      ],
    }),
    flightCrossSection: builder.query<FlightCrossSection, string[]>({
      query: ([callSign, departureDateUtc]) => ({
        url: "/v1/dashboard",
        method: "POST",
        body: {
          method: "get-cross-section",
          call_sign: callSign,
          departure_date_utc: departureDateUtc,
        },
      }),
      providesTags: (result, error, callSignAndDepartureDateUtc) => [
        {
          type: "Posts",
          PK: `flight#${callSignAndDepartureDateUtc[0]}`,
          SK: `account_date#${callSignAndDepartureDateUtc[0].slice(0, 3)}#${
            callSignAndDepartureDateUtc[1]
          }#CROSS_SECTION`,
        },
      ],
    }),
    manualInput: builder.mutation<FlightInfo, Post>({
      query: (body) => ({
        url: "/v1/dashboard",
        method: "POST",
        body: body,
      }),
      // Invalidates all queries that subscribe to this Post `id` only.
      // In this case, `getPost` will be re-run. `getPosts` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, { call_sign, departure_date_utc }) => [
        {
          type: "Posts",
          PK: `flight#${call_sign}`,
          SK: `account_date#${call_sign.slice(0, 3)}#${departure_date_utc}`,
        },
      ],
    }),
    deleteFlightInfo: builder.mutation<
      { success: boolean; call_sign: string; departure_date_utc: string },
      Pick<Post, "method" | "call_sign" | "departure_date_utc">
    >({
      query: (body) => ({
        url: "/v1/dashboard",
        method: "POST",
        body: body,
      }),
      invalidatesTags: (result, error, { call_sign, departure_date_utc }) => [
        {
          type: "Posts",
          PK: `flight#${call_sign}`,
          SK: `account_date#${call_sign.slice(0, 3)}#${departure_date_utc}`,
        },
      ],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useFlightListQuery,
  useFlightInfoQuery,
  useFlightDetailQuery,
  useFlightWindQuery,
  useFlightCrossSectionQuery,
  useManualInputMutation,
  useDeleteFlightInfoMutation,
} = dashboardApi;
